import { Controller } from "@hotwired/stimulus"
import { Sortable } from "@shopify/draggable"

/**
 * Sortable controller for drag-and-drop reordering
 * 
 * Connects to data-controller="sortable"
 * 
 * Values:
 *   - resourceName: Name of the resource being sorted (e.g., "route_location", "item")
 *     Set via data-sortable-resource-name-value="route_location"
 * 
 * Targets:
 *   - Items: [data-sortable-item] with data-url attribute
 *   - Handles: [data-sortable-handle] (optional drag handles)
 * 
 * Example:
 *   <div data-controller="sortable" data-sortable-resource-name-value="route_location">
 *     <div data-sortable-item data-url="/route_locations/1">...</div>
 *   </div>
 */
export default class extends Controller {
  static values = {
    resourceName: { type: String, default: "item" }
  }

  connect() {
    this.positionSyncFrame = null
    this.draggedItem = null
    this.draggedMirror = null

    this.sortable = new Sortable(this.element, {
      draggable: "[data-sortable-item]",
      handle: "[data-sortable-handle]",
      mirror: {
        constrainDimensions: true
      }
    })

    this.sortable.on("drag:start", this.handleDragStart)
    this.sortable.on("drag:stop", this.handleDragStop)
    this.sortable.on("mirror:created", this.handleMirrorCreated)
    this.sortable.on("mirror:destroy", this.handleMirrorDestroy)
    this.sortable.on("sortable:sorted", this.schedulePositionSync)
    this.sortable.on("sortable:stop", this.reorder)
    this.syncPositionLabels()
  }

  disconnect() {
    if (this.positionSyncFrame) {
      cancelAnimationFrame(this.positionSyncFrame)
      this.positionSyncFrame = null
    }

    this.sortable?.destroy()
  }

  handleDragStart = (event) => {
    const source = event.source

    this.draggedItem = source || null
    this.draggedMirror = null

    if (source) {
      const width = source.offsetWidth
      source.style.setProperty("--draggable-source-width", `${width}px`)

      requestAnimationFrame(() => {
        const mirror = this.draggedMirror || document.querySelector(".draggable-mirror")

        if (mirror) {
          mirror.style.width = `${width}px`
          mirror.style.minWidth = `${width}px`
        }
      })
    }
  }

  handleDragStop = () => {
    this.draggedItem = null
    this.draggedMirror = null
  }

  handleMirrorCreated = (event) => {
    this.draggedMirror = event.mirror || null
    this.syncPositionLabels()
  }

  handleMirrorDestroy = () => {
    this.draggedMirror = null
  }

  itemElements() {
    return Array
      .from(this.element.children)
      .filter((child) => child.matches?.("[data-sortable-item]"))
  }

  syncPositionLabels(items = this.itemElements()) {
    items.forEach((item, index) => {
      const positionElement = item.querySelector("[data-sortable-position]")

      if (positionElement) {
        positionElement.textContent = index + 1
      }
    })

    const draggedIndex = this.draggedItem ? items.indexOf(this.draggedItem) : -1

    if (this.draggedMirror && draggedIndex >= 0) {
      const mirrorPositionElement = this.draggedMirror.querySelector("[data-sortable-position]")

      if (mirrorPositionElement) {
        mirrorPositionElement.textContent = draggedIndex + 1
      }
    }
  }

  schedulePositionSync = () => {
    if (this.positionSyncFrame) {
      cancelAnimationFrame(this.positionSyncFrame)
    }

    this.positionSyncFrame = requestAnimationFrame(() => {
      this.positionSyncFrame = null
      this.syncPositionLabels()
    })
  }

  async persistPositions(items, token, positionOffset = 0) {
    for (let index = 0; index < items.length; index++) {
      const item = items[index]
      const url = item.dataset.url

      if (!url) continue

      const position = index + 1 + positionOffset

      try {
        const response = await fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": token,
            Accept: "application/json",
          },
          body: JSON.stringify({ [this.resourceNameValue]: { position } }),
        })

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`)
        }
      } catch (error) {
        const phase = positionOffset === 0 ? "position" : "temporary position"
        console.error(`Failed to update ${this.resourceNameValue} ${phase}:`, error)
      }
    }
  }

  reorder = async (event) => {
    // Only reorder if position actually changed
    const oldIndex = event.data?.oldIndex
    const newIndex = event.data?.newIndex
    
    if (oldIndex !== undefined && newIndex !== undefined && oldIndex === newIndex) {
      return
    }

    await new Promise((resolve) => requestAnimationFrame(() => resolve()))

    const items = this.itemElements()
    this.syncPositionLabels(items)

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    if (!token) {
      return
    }

    await this.persistPositions(items, token, 1000)
    await this.persistPositions(items, token)

    this.syncPositionLabels(items)
  }
}
